import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Clock, FileText, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { getStatusLabel } from '../../utils/helpers';

export const MobileHomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isShared = user?.is_shared || user?.role === 'SHARED';
  const [recentWorkOrders, setRecentWorkOrders] = useState([]);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadRecentWorkOrders(); }, []);
  useEffect(() => {
    // For shared kiosk accounts, surface the tenant name in the welcome card
    if (isShared) {
      api.getCompanySettings()
        .then(res => setCompanyName(res.data?.company_name || ''))
        .catch(() => {});
    }
  }, [isShared]);

  const loadRecentWorkOrders = async () => {
    try {
      const response = await api.getWorkOrders();
      // Backend already filters to current user's work orders (own + on-behalf-of)
      setRecentWorkOrders(response.data.slice(0, 10));
    } catch (error) {
      console.error('Failed to load work orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const map = {
      DRAFT: 'bg-slate-100 text-slate-600',
      IN_REVIEW: 'bg-amber-100 text-amber-700',
      NEEDS_CORRECTION: 'bg-red-100 text-red-700',
      APPROVED: 'bg-green-100 text-green-700',
      INVOICED: 'bg-blue-100 text-blue-700',
      CANCELLED: 'bg-slate-100 text-slate-400',
    };
    return map[status] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="px-4 py-6 space-y-6" data-testid="mobile-home-page">
      {/* Welcome */}
      <div data-testid="mobile-welcome">
        {isShared ? (
          <>
            <h2 className="text-xl font-bold text-slate-900" data-testid="mobile-company-name">
              {companyName || user?.first_name || 'Account Condiviso'}
            </h2>
            <p className="text-sm text-slate-500">Dispositivo aziendale — seleziona l&apos;operatore al momento dell&apos;inserimento.</p>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold text-slate-900">Ciao, {user?.first_name || 'Utente'}!</h2>
            <p className="text-sm text-slate-500">Cosa vuoi fare oggi?</p>
          </>
        )}
      </div>

      {/* Main Action */}
      <button
        onClick={() => navigate('/m/bollettini/nuovo')}
        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl p-6 shadow-lg active:scale-[0.98] transition-transform"
        data-testid="new-workorder-btn"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Plus className="h-6 w-6" />
          </div>
          <div className="text-left">
            <div className="font-bold text-lg">Nuovo Bollettino</div>
            <div className="text-blue-100 text-sm">Crea un nuovo bollettino di lavoro</div>
          </div>
        </div>
      </button>

      {/* Time Registry Placeholder */}
      <button
        onClick={() => alert('Registro ore - Coming soon!')}
        className="w-full bg-white border-2 border-dashed border-slate-300 text-slate-500 rounded-2xl p-5 hover:border-slate-400 transition-colors"
        data-testid="time-registry-btn"
      >
        <div className="flex items-center gap-3">
          <Clock className="h-5 w-5" />
          <div className="text-left">
            <div className="font-medium text-slate-700">Registro Ore</div>
            <div className="text-xs">Prossimamente disponibile</div>
          </div>
        </div>
      </button>

      {/* Recent Work Orders */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-900">I tuoi bollettini</h3>
          <button onClick={() => navigate('/m/bollettini')} className="text-blue-600 text-sm font-medium flex items-center gap-1" data-testid="view-all-btn">
            Vedi tutti <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-slate-400">Caricamento...</div>
        ) : recentWorkOrders.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-xl border border-slate-200">
            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">Nessun bollettino</p>
            <p className="text-slate-400 text-xs mt-1">Crea il tuo primo bollettino!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {recentWorkOrders.map((wo) => (
              <button
                key={wo.id}
                onClick={() => navigate(`/m/bollettini/${wo.id}`)}
                className="w-full bg-white rounded-xl p-4 border border-slate-200 text-left hover:border-slate-300 transition-colors"
                data-testid={`wo-card-${wo.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm text-slate-900">{wo.number}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(wo.status)}`}>
                    {getStatusLabel(wo.status)}
                  </span>
                </div>
                <p className="text-sm text-slate-600">{wo.customer_name}</p>
                {wo.site_name && <p className="text-xs text-slate-400 mt-0.5">{wo.site_name}</p>}
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-slate-400">{new Date(wo.created_at).toLocaleDateString('it-CH')}</span>
                  {wo.status === 'DRAFT' && <span className="text-xs text-amber-600 font-medium">Da inviare</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileHomePage;
